import { BLOCKS_PATH } from "../../../shared/worksheets/constants";
import { showBlockDiffModal } from "../components/BlockDiffModal";
import { pseudoBlocksToWorksheetBlocks } from "../context/pseudo-blocks";
import { AIPseudoBlockType, worksheetSchema } from "../schemas/worksheet-schema";

import { ToolType } from "./types";

import { t } from "i18n/i18n";
import { docCache } from "sharedb/ShareDBDoc";
import { store } from "store/index";
import { selectPages } from "store/selectors";
import { BROADCAST_TOPICS, sendBroadcast } from "utils/broadcast";

/**
 * A tool that accepts a list of pseudo blocks, converts them to worksheet blocks,
 * and adds them to the current Page.
 */
export const modifyWorksheet = async (params: { blocks: AIPseudoBlockType[] }) => {
  const state = store.getState();
  const doc = docCache.current;
  if (!doc) {
    throw new Error("Worksheet for current Page not found");
  }
  const pageId = selectPages(state).find((p) => p.worksheet_id === doc?.id)?.id;
  if (!pageId) {
    throw new Error("Current Page not found");
  }

  const { blocks } = params;

  if (blocks.length === 0) {
    throw new Error("No blocks to add.");
  }

  const newBlocks = pseudoBlocksToWorksheetBlocks(blocks);
  const newBlocksWithOldBlocks = [...doc.data.b, ...newBlocks];

  const accepted = await showBlockDiffModal(doc, newBlocksWithOldBlocks);
  if (!accepted) {
    return t("ai.modification_rejected");
  }

  const blockInsertOps = newBlocks.map((block, i) => ({
    p: [BLOCKS_PATH, doc.data[BLOCKS_PATH].length + i],
    li: block,
  }));

  await new Promise((resolve, reject) => {
    doc.submitOp(blockInsertOps, {}, (err) => {
      if (err) {
        reject(new Error(`${err.code}: ${err.message}`));
      }
      resolve(true);
    });
  });

  // This allows for animation on the Page to show that blocks have been added
  sendBroadcast(BROADCAST_TOPICS.AI_PAGE_ENHANCED, { pageId });

  return t("ai.modification_accepted");
};

export const modifyWorksheetTool: ToolType<{
  blocks: AIPseudoBlockType[];
}> = {
  name: "modify_page_content",
  description: [
    "Generate new content or suggestions for the current Page.",
    "Any blocks that need to be kept the same should be included as-is in the response.",
    "The user will be prompted to accept or reject changes, so this action can be taken without risk at any time.",
  ].join("\n"),
  progressMessage: "Generating content for the current page...",
  parameters: worksheetSchema,
  callback: modifyWorksheet,
};
